import React, { useCallback, useEffect, useRef } from "react";
import QRCodeStyling from "qr-code-styling";
import AttributeField from "./AttributeField";
import {
  QR_CODE_TYPE_OPTIONS,
  QR_STYLES,
  QR_TYPES,
  updateVariableAttr,
} from "../../../common/CertificateOnboarding/utils/variables";
import { CARD_CLASS } from "../../../common/CertificateOnboarding/utils/styles";

function mapQrStyleToDotsType(style) {
  if (!style) return "extra-rounded";
  const normalized = String(style).toLowerCase();
  if (QR_STYLES.includes(normalized)) return normalized;
  return "extra-rounded";
}

function buildDotsOptions(attrs) {
  const qrType = attrs["data-qr-type"] || "linear";
  const color1 = attrs["data-qr-color1"] || "#000000";
  const styleType = mapQrStyleToDotsType(attrs["data-qr-style"]);

  if (qrType === "gradient") {
    return {
      type: styleType,
      gradient: {
        type: "linear",
        rotation: 0,
        colorStops: [
          { offset: 0, color: color1 },
          { offset: 1, color: attrs["data-qr-color2"] || "#3538cd" },
        ],
      },
    };
  }

  return {
    type: styleType,
    color: color1,
  };
}

function createQrInstance(dotsOptions) {
  return new QRCodeStyling({
    width: 200,
    height: 200,
    data: "https://app.credissuer.com",
    dotsOptions,
    backgroundOptions: { color: "#ffffff" },
    cornersSquareOptions: { type: "extra-rounded" },
  });
}

function QrVariableEditor({
  variableKey,
  attrs,
  setVariableConfig,
  qrCodeType,
  onQrCodeTypeChange,
  t,
}) {
  const containerRef = useRef(null);
  const qrInstanceRef = useRef(null);
  const lastQrTypeRef = useRef(attrs["data-qr-type"] || "linear");
  const debounceRef = useRef(null);
  const isGradient = (attrs["data-qr-type"] || "linear") === "gradient";

  const onAttrChange = (attrKey) => (value) => {
    updateVariableAttr(setVariableConfig, variableKey, attrKey, value);
  };

  const qrTypeOptions = QR_TYPES.map((val) => ({
    value: val,
    label: t(`certificateOnboarding:qr_type_${val}`),
  }));

  const qrStyleOptions = QR_STYLES.map((val) => ({
    value: val,
    label: val,
  }));

  const qrEncodingOptions = QR_CODE_TYPE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`certificateOnboarding:${opt.labelKey}`),
  }));

  const mountOrUpdateQr = useCallback(
    (forceRecreate = false) => {
      const container = containerRef.current;
      if (!container) return;

      const dotsOptions = buildDotsOptions(attrs);
      const currentType = attrs["data-qr-type"] || "linear";
      const typeChanged = lastQrTypeRef.current !== currentType;

      if (typeChanged) {
        lastQrTypeRef.current = currentType;
        forceRecreate = true;
      }

      if (forceRecreate || !qrInstanceRef.current) {
        container.replaceChildren();
        qrInstanceRef.current = createQrInstance(dotsOptions);
        qrInstanceRef.current.append(container);
        return;
      }

      qrInstanceRef.current.update({
        data: "https://app.credissuer.com",
        dotsOptions,
      });
    },
    [attrs],
  );

  useEffect(() => {
    const qrType = attrs["data-qr-type"] || "linear";
    const isTypeChange = lastQrTypeRef.current !== qrType;

    if (isTypeChange) {
      clearTimeout(debounceRef.current);
      mountOrUpdateQr(true);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      mountOrUpdateQr(false);
    }, 120);

    return () => clearTimeout(debounceRef.current);
  }, [
    attrs["data-qr-type"],
    attrs["data-qr-color1"],
    attrs["data-qr-color2"],
    attrs["data-qr-style"],
    mountOrUpdateQr,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      qrInstanceRef.current = null;
    };
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <AttributeField
          id={`${variableKey}-qr-encoding-type`}
          label={t("certificateOnboarding:qr_encoding_type_label")}
          type="select"
          value={qrCodeType}
          onChange={onQrCodeTypeChange}
          options={qrEncodingOptions}
        />

        <AttributeField
          id={`${variableKey}-qr-name`}
          label={t("certificateOnboarding:attr_data_name")}
          value={attrs["data-name"]}
          onChange={onAttrChange("data-name")}
        />

        <AttributeField
          id={`${variableKey}-qr-style`}
          label={t("certificateOnboarding:attr_data_qr_style")}
          type="select"
          value={attrs["data-qr-style"] ?? "extra-rounded"}
          onChange={onAttrChange("data-qr-style")}
          options={qrStyleOptions}
        />

        <AttributeField
          id={`${variableKey}-qr-type`}
          label={t("certificateOnboarding:attr_data_qr_type")}
          type="select"
          value={attrs["data-qr-type"] ?? "linear"}
          onChange={onAttrChange("data-qr-type")}
          options={qrTypeOptions}
        />

        <AttributeField
          id={`${variableKey}-qr-color1`}
          label={t("certificateOnboarding:attr_data_qr_color1")}
          type="color"
          value={attrs["data-qr-color1"] ?? "#000000"}
          onChange={onAttrChange("data-qr-color1")}
        />

        {isGradient && (
          <AttributeField
            id={`${variableKey}-qr-color2`}
            label={t("certificateOnboarding:attr_data_qr_color2")}
            type="color"
            value={attrs["data-qr-color2"] ?? "#3538cd"}
            onChange={onAttrChange("data-qr-color2")}
          />
        )}
      </div>

      <div
        className={`${CARD_CLASS} p-6 flex flex-col items-center justify-center min-h-[280px]`}
      >
        <p className="text-sm font-medium text-cfHeadingText mb-4">
          {t("certificateOnboarding:qr_preview_title")}
        </p>
        <div
          ref={containerRef}
          className="flex items-center justify-center min-h-[200px] min-w-[200px] [&_canvas]:max-w-full [&_canvas]:h-auto"
        />
      </div>
    </div>
  );
}

export default QrVariableEditor;
